import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Why are bond yields suddenly higher than dividend yields?',
    a: 'Starting in 2022, the Federal Reserve raised the federal funds rate from near zero to over 5% to fight inflation. Treasury yields rose alongside. As of early 2026, the 10-year Treasury yields around 4.3%, while the S&P 500 dividend yield is about 1.4% and most quality dividend stocks pay 2-4%. The math reversed: bonds out-yield stocks for the first time in 15+ years.',
  },
  {
    q: 'So should I sell my dividend stocks and buy bonds?',
    a: 'Not really — they\'re different tools. A 10-year Treasury at 4.3% gives you a LOCKED yield, paid as ordinary income, with NO growth. A 3% dividend stock with a 7% dividend growth rate will yield more than 4.3% on your original cost within 5-6 years AND keep growing forever. Plus qualified dividends are taxed at 0%, 15%, or 20% federal — usually less than the ordinary-income rate on bond interest.',
  },
  {
    q: 'When does cash actually beat stocks?',
    a: 'When you need the money in under 3-5 years. Emergency fund? Cash or T-bills. Down payment in 2027? Lock in a CD or short Treasury — the yield is real and the principal is safe. Retirement income that needs to last 30 years? Dividend stocks win on growth and tax treatment over that horizon every historical period we have data for.',
  },
  {
    q: 'What about the 60/40 portfolio?',
    a: 'The classic 60% stocks / 40% bonds portfolio is a different conversation — it\'s about diversification and reducing drawdowns, not just yield. With bond yields now competitive, the 40% bond sleeve does its diversification job AND pays meaningfully. That\'s a stronger version of 60/40 than we\'ve had in 15 years.',
  },
];

export default function LearnBondsVsDividends() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="Bond Yields vs Dividend Yields — When Cash Beats Stocks (And When It Doesn't) | DiviDrip"
      description="The 10-year Treasury yields more than most dividend stocks. So why not just buy bonds? The math on growth, taxes, and the 30-year total-return story that almost always favors quality dividend payers."
      canonical="https://twylightcrow.com/learn/bond-yields-vs-dividend-yields"
      guidePath="/learn/bond-yields-vs-dividend-yields"
      sectionLabel="Strategy & Psychology"
      sectionAnchor="strategy"
      kicker="Learn · Macro context"
      h1="Bond Yields vs Dividend Yields — When Cash Beats Stocks (And When It Doesn't)"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/four-phases', label: 'The 4 phases of every dividend investor' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/why-drip-roth', label: 'Why DRIP belongs in a Roth IRA' },
        { to: '/learn/sector-rotation-for-dividend-investors', label: 'Sector rotation playbook' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — the tax math' },
      ]}
    >
      <p>
        For the first time in fifteen years, you can earn more interest on a US
        Treasury bond than dividends on the S&amp;P 500. The 10-year Treasury sits
        around <strong>4.3%</strong>. The S&amp;P 500 dividend yield is about{' '}
        <strong>1.4%</strong>. Even quality dividend stocks like SCHD yield about{' '}
        <strong>3.6%</strong>. Bonds win on yield, full stop.
      </p>
      <p>
        So why not just sell every dividend stock and load up on Treasuries?
        Because yield is only one chapter in a much longer story. Let&apos;s read
        the rest.
      </p>

      <h2>The 5-year reality check</h2>
      <p>
        Put $10,000 into each option today. Don&apos;t touch it. Compare in 5 years.
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>Vehicle</th><th>Year 1 income</th><th>Year 5 income</th><th>Principal change</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>10-yr Treasury @ 4.3%</td>
            <td>$430</td>
            <td>$430 (fixed)</td>
            <td>Same $10,000 face value (locked)</td>
          </tr>
          <tr>
            <td>SCHD @ 3.6% starting yield, 8% div growth</td>
            <td>$360</td>
            <td>~$529</td>
            <td>~+30% to ~+50% over 5 yrs (historical SCHD price return)</td>
          </tr>
          <tr>
            <td>KO @ 3.0% starting yield, 5% div growth</td>
            <td>$300</td>
            <td>~$383</td>
            <td>Variable, but historically positive</td>
          </tr>
        </tbody>
      </table>
      <p>
        The bond gives you certainty. The dividend stocks give you compounding.
        By year 4 or 5, the dividend stocks have caught up on yield AND added
        share-price appreciation on top. Over 10-15 years the gap widens
        dramatically — this is the magic of <Link to="/learn/yield-on-cost">yield
        on cost</Link>.
      </p>

      <h2>The tax angle that always gets ignored</h2>
      <p>
        Bond interest is taxed as ORDINARY income — your full marginal rate.
        Qualified dividends from US stocks are taxed at long-term capital-gain
        rates: 0%, 15%, or 20% depending on your bracket.
      </p>
      <ul>
        <li>Single filer earning $75,000? Bond interest taxed at 22% federal. Qualified dividends at 15%.</li>
        <li>Married filing jointly under ~$95,000 taxable? Qualified dividends are <strong>tax-free</strong>. Bond interest still taxed at 12-22%.</li>
        <li>State taxes apply differently — Treasury interest is exempt from state tax (a real benefit in CA/NY), but corporate-bond interest is fully state-taxed.</li>
      </ul>
      <p>
        For a typical retiree with $75k-$150k of taxable income, the after-tax
        spread between a 4.3% Treasury and a 3.6% qualified-dividend stock is
        often within 0.3-0.5%. The dividend stock&apos;s growth makes up the rest
        within 2-3 years. See{' '}
        <Link to="/learn/qualified-vs-ordinary-dividends">qualified vs ordinary dividends</Link>{' '}
        for the full breakdown.
      </p>

      <h2>When cash actually wins</h2>
      <p>
        We&apos;re not anti-bond. There&apos;s a clear &quot;cash beats stocks&quot;
        zone, and it&apos;s about TIME HORIZON:
      </p>
      <ul>
        <li><strong>0-2 years</strong> — Cash, T-bills, HYSA, money-market. Yield is real, principal is safe, no equity drawdown risk.</li>
        <li><strong>2-5 years</strong> — Short Treasuries, CDs, intermediate bond funds. Locking in 4-5% yields for a down payment or planned expense is genuinely smart right now.</li>
        <li><strong>5-10 years</strong> — Mixed. Some bonds for stability, dividend equities for the growth tail. The classic 60/40 makes more sense than it has in years.</li>
        <li><strong>10+ years</strong> — Dividend stocks pull ahead in almost every historical period. The growth + qualified-dividend tax treatment compounds into a far larger total return.</li>
      </ul>

      <h2>The 30-year story</h2>
      <p>
        Roll back the clock 30 years. In 1996 a 10-year Treasury yielded about
        6.5%. The S&amp;P 500 dividend yield was about 2.1%. Locking in 6.5% for
        a decade looked unbeatable — and for that decade, it WAS. But the company
        behind the dividend — Coca-Cola, Procter &amp; Gamble, Johnson &amp;
        Johnson — kept raising. By 2006 the yield-on-cost of those positions had
        passed 6.5%. By 2016 it was past 10%. By 2026, well past 15%. The bond
        stopped paying after 10 years. The dividend stocks are still paying — and
        the original $10,000 is now worth six figures.
      </p>
      <p>
        That&apos;s the trade. Bonds win the next year. Quality dividend growth
        stocks win the next thirty.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Bottom line</h2>
      <p>
        Yes, bonds out-yield stocks today. No, that doesn&apos;t mean you sell your
        dividend stocks. Use bonds for what they&apos;re GOOD at — short-horizon
        money, drawdown protection, the 40% sleeve of a 60/40 portfolio. Use
        dividend equities for what THEY&apos;RE good at — growing income, qualified
        tax treatment, the 30-year compounding tail. They&apos;re tools, not rivals.
      </p>
    </LearnPage>
  );
}
